import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import CaseStudyHero from '../../components/CaseStudyHero';
import TestimonialCarousel from '../../components/TestimonialCarousel';
import Button from '../../components/Button';
import WidthWrapper from '../../components/WidthWrapper';
import ImageRow from '../../components/ImageRow';
import { TestimonialCarouselItem } from '../../components/TestimonialCarousel';
import StatRow from '../../components/StatRow';
import { Seo } from '../../components/Seo';
import Heading from '../../components/Heading';
import sortByNewest from '../../hooks/sortByNewest';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import { getImage } from 'gatsby-plugin-image';
import OpenGraph from '../../images/open-graph.png';
import VerticalVideos from '../../components/VerticalVideos';
import handleScroll from '../../hooks/handleScroll';
import { PrismicRichText } from '@prismicio/react';

const StyledButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-bottom: 4.5626rem;
`;

const CaseStudyName = ({ data }) => {
  const study = data?.prismicCaseStudy.data;
  const isGated = study.gated === null ? true : study.gated;
  const contentTitle = data.prismicCaseStudy.data.title;
  const tags = data?.prismicCaseStudy.data.tags.map((item) => item.tag.document.data.title);

  const relatedCaseStudies = sortByNewest(
    data.allPrismicCaseStudy.nodes
      .filter((item) => {
        return (
          item.data.title !== contentTitle &&
          item.data.tags.some((item) => {
            return tags.includes(item.tag.document.data.title);
          })
        );
      })
      .map((item) => ({
        image: getImage(item.data.hero_image?.gatsbyImageData)
          ? getImage(item.data.hero_image.gatsbyImageData)
          : 'https://images.prismic.io/bcvsocial/04247e7c-56ac-443d-91d8-113cb1e91444_191008_JW-Marriott-DC_GW_Pre-Selects-25-1-e1575570515666.jpg?ixlib=gatsbyFP&auto=compress%2Cformat&fit=max',
        alt: item.data.hero_image?.alt ? item.hero_image?.alt : 'Related content hero image',
        title: item.data.title,
        type: 'case-studies',
        date: item.data.date_posted ? item.data.date_posted : item.first_publication_date,
      }))
  ).slice(0, 3);

  const featuredImages = study.featured_images.map((img) => {
    const image = getImage(img.image.gatsbyImageData);
    return { image: image, alt: img.image?.alt };
  });

  const stats = study.what_we_saw.map((stat) => {
    return { stat: stat.stat_figure.text, metric: stat.metric_description };
  });

  return (
    <Layout CTA border>
      <CaseStudyHero
        headingText={study.title}
        contentSummary={study.content_summary}
        websiteLink={study.hotel_link?.url}
        locationLink={study.map_link?.url}
        locationText="THERE IS NO LOCATION QUERY"
        heroImage={getImage(study.hero_image.gatsbyImageData)}
        video={study.optional_video_hero?.url}
        caseStudy={study}
        isGated={isGated}
      />
      {study.case_study.url && (
        <StyledButtonWrapper className="button-wrapper">
          <Button
            text="Download Case Study"
            onClick={isGated ? handleScroll : undefined}
            link={isGated ? undefined : study.case_study.url}
          />
        </StyledButtonWrapper>
      )}
      {featuredImages.length > 0 && <ImageRow images={featuredImages} />}
      {stats.length > 0 && <StatRow heading={'WHAT WE SAW'} stats={stats} />}
      {study.videolist.length > 1 && (
        <WidthWrapper>
          <VerticalVideos videoList={study.videolist} />
        </WidthWrapper>
      )}
      {study.testimonials[0].author.text && (
        <TestimonialCarousel controls={study.testimonials.length > 1}>
          {study.testimonials.map((testimonial) => {
            return (
              <TestimonialCarouselItem
                quote={<PrismicRichText field={testimonial.quote.richText} />}
                citation={testimonial.author.text}
              />
            );
          })}
        </TestimonialCarousel>
      )}
      <Heading headingLevel={3} headingText="Related content" withLine />
      <ImageRow images={relatedCaseStudies} />
    </Layout>
  );
};

export default CaseStudyName;

export const Head = ({ data }) => (
  <Seo title={data.prismicCaseStudy.data.title}>
    <meta property="og:title" content={data.prismicCaseStudy.data.title} />
    <meta property="og:image" content={OpenGraph} />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:type" content="website" />
  </Seo>
);

export const CaseStudyQuery = graphql`
  query ($id: String!) {
    prismicCaseStudy(id: { eq: $id }) {
      id
      data {
        gated
        learn_more_subheading {
          text
        }
        gated_iframe_link {
          url
        }
        title
        content_summary {
          richText
        }
        hotel_link {
          url
        }
        hero_image {
          gatsbyImageData(placeholder: BLURRED, width: 2000, height: 1400)
          alt
        }
        optional_video_hero {
          url
        }
        case_study {
          url
        }
        testimonials {
          author {
            text
          }
          quote {
            richText
          }
        }
        featured_images {
          image {
            gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
            alt
          }
        }
        what_we_saw {
          metric_description {
            text
            richText
          }
          stat_figure {
            text
          }
        }
        tags {
          tag {
            document {
              ... on PrismicTag {
                data {
                  title
                  topic {
                    document {
                      ... on PrismicTopic {
                        data {
                          title
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        videolist {
          video {
            url
          }
        }
      }
    }
    allPrismicCaseStudy {
      nodes {
        data {
          title
          hero_image {
            gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
            alt
          }
          date_posted(formatString: "MMMM DD, YYYY")
          tags {
            tag {
              document {
                ... on PrismicTag {
                  data {
                    title
                  }
                }
              }
            }
          }
        }
        type
        first_publication_date(formatString: "MMMM DD, YYYY")
      }
    }
  }
`;
